/**
 * Identifier types and their formats.
 *
 * Ids are plain strings with a recognisable prefix so they can be told apart in logs and JSON, and so
 * literals type-check without casts in tests (`'p1'` is a `PlayerId`).
 *
 * Object ids are **opaque** (a keyed permutation of a counter, `core/alloc.ts`): a visibly sequential
 * id would reveal when a hidden card entered its zone (e.g. which turn a revealed hand card was drawn).
 * They are unique for the whole game. Every move into or out of a non-board zone allocates a new id
 * (CR 124, docs/design/03 §3.1), so an id seen in a public zone can never be traced back to a hidden one.
 */

/** A seat in the match: `p1`, `p2`, … in seating order. The host maps users to seats. */
export type PlayerId = `p${number}`;

/** A game object incarnation (card, token, rune, legend, battlefield). `o` + 6 base-36 characters. */
export type ObjectId = `o${string}`;

/** A battlefield location (stable for the whole game, even if its card is replaced: CR 439). */
export type BattlefieldId = `bf${number}`;

/** A chain item (card or ability on the chain, CR 327-331). */
export type ChainItemId = `c${number}`;

/** A pending decision; commands answering it must quote this id (docs/design/03 §4). */
export type DecisionId = `d${number}`;

/** Rules identity of a card: the Runebound `card.id` slug of the base card (aliases folded by @play/catalog). */
export type CardId = string;

/** A Runebound printing id (`ogn-202a-298`): which art to show. Cosmetic only, never read by rules. */
export type PrintingId = string;

/** A token kind (CR 187), namespaced so it can never collide with a catalogue card id. */
export type TokenId = `token:${string}`;

export const PLAYER_ID_PATTERN = /^p[1-9][0-9]?$/;
export const OBJECT_ID_PATTERN = /^o[0-9a-z]{6}$/;
export const BATTLEFIELD_ID_PATTERN = /^bf[0-9]{1,3}$/;
export const CHAIN_ITEM_ID_PATTERN = /^c[0-9]{1,9}$/;
export const DECISION_ID_PATTERN = /^d[0-9]{1,9}$/;
export const TOKEN_ID_PATTERN = /^token:[a-z][a-z0-9-]{0,40}$/;

/** The id of seat `index` (0-based): 0 → `p1`. */
export function playerIdAt(index: number): PlayerId {
  return `p${index + 1}`;
}

/** Whether a string is a well-formed player id. */
export function isPlayerId(value: string): value is PlayerId {
  return PLAYER_ID_PATTERN.test(value);
}

/** Whether a string is a well-formed object id. */
export function isObjectId(value: string): value is ObjectId {
  return OBJECT_ID_PATTERN.test(value);
}

/** Whether a string is a well-formed battlefield id. */
export function isBattlefieldId(value: string): value is BattlefieldId {
  return BATTLEFIELD_ID_PATTERN.test(value);
}

/** Whether a string is a well-formed token id. */
export function isTokenId(value: string): value is TokenId {
  return TOKEN_ID_PATTERN.test(value);
}

/** Who may see something: everyone (players and spectators) or a list of seats. */
export type Audience = 'all' | PlayerId[];

/** True when `viewer` is in the audience. Spectators are only in `'all'`. */
export function inAudience(audience: Audience, viewer: PlayerId | 'spectator'): boolean {
  return audience === 'all' || (viewer !== 'spectator' && audience.includes(viewer));
}
